/**
 * Bingo Detail Page
 *
 * Displays a single bingo card with cells, progress, and actions.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings } from 'lucide-react';
import {
  BingoCardComponent,
  BingoProgressDashboard,
  BingoCelebration,
} from '@/components/bingo';
import {
  useBingoCard,
  useBingoCells,
  useBingoPatterns,
  useBingoProgress,
  useCompleteBingoCell,
} from '@/hooks/use-bingo';
import { Button } from '@/components/ui';
import type { BingoCell, BingoPattern } from '@/types/bingo';

export function BingoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [newPattern, setNewPattern] = useState<BingoPattern | null>(null);
  const [previousPatternCount, setPreviousPatternCount] = useState(0);

  const { data: card, isLoading: cardLoading } = useBingoCard(id!);
  const { data: cellsData, isLoading: cellsLoading } = useBingoCells(id!);
  const { data: patternsData } = useBingoPatterns(id!);
  const { data: progress } = useBingoProgress(id!);
  const completeCellMutation = useCompleteBingoCell();

  const cells = cellsData?.cells || [];
  const patterns = patternsData?.patterns || [];

  // Detect new patterns
  useEffect(() => {
    if (patterns.length > previousPatternCount) {
      const newPatterns = patterns.slice(previousPatternCount);
      if (newPatterns.length > 0) {
        setNewPattern(newPatterns[0]);
        setPreviousPatternCount(patterns.length);
      }
    } else if (previousPatternCount === 0) {
      setPreviousPatternCount(patterns.length);
    }
  }, [patterns, previousPatternCount]);

  const handleCellComplete = async (cell: BingoCell) => {
    if (cell.status === 'completed') return;
    
    try {
      await completeCellMutation.mutateAsync({
        cardId: id!,
        cellId: cell.id,
      });
    } catch (error) {
      console.error('Error completing cell:', error);
    }
  };

  const handleCellClick = (cell: BingoCell) => {
    // Could open a detail modal here
    console.log('Cell clicked:', cell);
  };

  if (cardLoading || cellsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading bingo card...</div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500">Card not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 -m-6 p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/bingo')}
            className="flex items-center gap-2 hover:bg-white/50 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button variant="ghost" className="flex items-center gap-2 hover:bg-white/50 backdrop-blur-sm">
            <Settings className="w-4 h-4" />
            Settings
          </Button>
        </div>

        {/* Celebration */}
        {newPattern && (
          <BingoCelebration
            patternType={newPattern.patternType}
            patternIndex={newPattern.patternIndex}
            onComplete={() => setNewPattern(null)}
          />
        )}

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Bingo Card - Takes 2 columns */}
          <div className="lg:col-span-2">
            <BingoCardComponent
              card={card}
              cells={cells}
              patterns={patterns}
              onCellClick={handleCellClick}
              onCellComplete={handleCellComplete}
            />
          </div>

          {/* Sidebar - Progress Dashboard */}
          <div className="lg:col-span-1">
            {progress && <BingoProgressDashboard progress={progress} />}
          </div>
        </div>
      </div>
    </div>
  );
}

